import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Settings, Users, ShieldAlert, ChevronRight, ArrowLeft } from "lucide-react";
import PanelAdminSeguridad from "./PanelAdminSeguridad";

const ConfiguracionPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState("home");

  if (view === "seguridad") {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => setView("home")} data-testid="btn-back-config">
          <ArrowLeft className="w-4 h-4 mr-1" /> Volver a Configuración
        </Button>
        <PanelAdminSeguridad />
      </div>
    );
  }

  const isAdmin = user?.role === "admin";
  const isStaff = isAdmin || user?.role === "funcionario";

  const modules = [
    {
      key: "seguridad",
      title: "Seguridad del Sistema",
      description: "Audit log forense, API Keys, IPs bloqueadas, cola de emails y sesiones activas",
      icon: ShieldAlert,
      color: "red",
      badge: "Panel Admin",
      enabled: isStaff,
      action: () => setView("seguridad"),
    },
    {
      key: "usuarios",
      title: "Gestión de Funcionarios",
      description: "Crear, editar y desactivar funcionarios. Asignar permisos por módulo y resetear claves.",
      icon: Users,
      color: "blue",
      badge: isAdmin ? "Solo Admin" : "Requiere Admin",
      enabled: isAdmin,
      action: () => navigate("/admin/usuarios"),
    },
  ];

  return (
    <div className="space-y-6" data-testid="configuracion-page">
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
          <Settings className="w-6 h-6 text-slate-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Configuración</h1>
          <p className="text-slate-500">Configuración del sistema</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {modules.map((m) => {
          const Icon = m.icon;
          const clickable = m.enabled && typeof m.action === "function";
          return (
            <Card
              key={m.key}
              className={`group transition-all ${clickable ? "cursor-pointer hover:shadow-lg hover:-translate-y-0.5" : "opacity-75"}`}
              onClick={() => clickable && m.action()}
              data-testid={`config-module-${m.key}`}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className={`w-10 h-10 bg-${m.color}-100 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform`}>
                    <Icon className={`w-5 h-5 text-${m.color}-600`} />
                  </div>
                  {clickable && <ChevronRight className="w-4 h-4 text-slate-400" />}
                </div>
                <h3 className="font-semibold text-slate-900 mb-1">{m.title}</h3>
                <p className="text-xs text-slate-500 mb-2">{m.description}</p>
                <span className={`inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                  m.enabled ? `bg-${m.color}-100 text-${m.color}-700` : "bg-slate-100 text-slate-500"
                }`}>
                  {m.badge}
                </span>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {isAdmin && (
        <Card className="border-red-200 bg-gradient-to-br from-red-50 to-white">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-red-600" /> Panel de Administración de Seguridad
            </CardTitle>
            <CardDescription>
              Acceso completo al stack de ciberseguridad: Rate Limiting, Vault, WAF, RLS,
              Audit Log con hash encadenado, API Keys, y más.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => setView("seguridad")} className="bg-red-600 hover:bg-red-700" data-testid="btn-ir-panel-seguridad">
              <ShieldAlert className="w-4 h-4 mr-2" /> Abrir Panel de Seguridad
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ConfiguracionPage;
